import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { IconButton, BUTTON_VARIANTS } from 'ui/button';
import { Close16 } from 'ui/icons/action/Close16';
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from 'ui/workflows/drawer/Drawer';
import { Body2Bold, Subtitle1, colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import { useDrawersContext } from '../contexts';
import { selectListReferencesByName, selectWorkflowReferencesByName } from '../selectors';
import { DynamicLists, WorkflowReferencesByWorkflowId } from '../types';
import { ListReferenceListItem } from './ListReferenceListItem';
import { WorkflowReferenceListItem } from './WorkflowReferenceListItem';

const TEXT = {
  LIST_HEADING: 'List references',
  NONE: 'None',
  WORKFLOW_HEADING: 'Journey references',
};

const StyledDrawer = styled(Drawer)<{ navBarHeight: number }>`
  display: flex;
  height: calc(100vh - ${({ navBarHeight }) => `${navBarHeight}px`});
  position: fixed;
  right: 0;
  top: ${({ navBarHeight }) => `${navBarHeight}px`};
  width: ${pxToRem(525)};
  z-index: 11;
`;

const HeaderText = styled(Subtitle1)`
  color: ${colors.textSecondary};
  margin-bottom: 0;
`;

const Ul = styled.ul`
  padding-left: ${spacing.base};
  &:first-of-type {
    margin-bottom: ${spacing.threeQuarter};
  }
`;

const NoneText = styled.span`
  padding-left: ${spacing.base};
  &:first-of-type {
    margin-bottom: ${spacing.threeQuarter};
  }
`;

export const ReferencesDrawer = () => {
  const { eventName, hideDrawer } = useDrawersContext();
  const workflowReferencesByName = useSelector(selectWorkflowReferencesByName);
  const listReferencesByName = useSelector(selectListReferencesByName);
  const navBarHeight = useNavBarHeight();

  if (!eventName) return null;

  const workflowReferencesById: WorkflowReferencesByWorkflowId | undefined =
    workflowReferencesByName[eventName];
  const listReferences: DynamicLists | undefined = listReferencesByName[eventName];

  return (
    <StyledDrawer navBarHeight={navBarHeight}>
      <DrawerHeader>
        <HeaderText>{eventName}</HeaderText>
        <IconButton onClick={hideDrawer} variant={BUTTON_VARIANTS.GHOST}>
          <Close16></Close16>
        </IconButton>
      </DrawerHeader>
      <DrawerBody>
        <Body2Bold>{TEXT.LIST_HEADING}</Body2Bold>
        {listReferences ? (
          <Ul>
            {listReferences.map(({ id, name }) => (
              <ListReferenceListItem key={id} id={id} name={name}></ListReferenceListItem>
            ))}
          </Ul>
        ) : (
          <NoneText>{TEXT.NONE}</NoneText>
        )}
        <Body2Bold>{TEXT.WORKFLOW_HEADING}</Body2Bold>
        {workflowReferencesById ? (
          <Ul>
            {Object.values(workflowReferencesById).map(
              ({ workflowId, workflowName, workflowNodes }) => (
                <WorkflowReferenceListItem
                  key={workflowId}
                  workflowId={workflowId}
                  workflowName={workflowName}
                  workflowNodes={workflowNodes}
                ></WorkflowReferenceListItem>
              ),
            )}
          </Ul>
        ) : (
          <NoneText>{TEXT.NONE}</NoneText>
        )}
      </DrawerBody>
      <DrawerFooter></DrawerFooter>
    </StyledDrawer>
  );
};
